import { ColumnMappingData, SchemaData, ExcelData } from '../../pages/ExcelUploadPage';
import { excelSchemaBody } from '@carboncare/schemas';
import { preflight } from './schemaPreflight';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class ExcelApi {
  private uploadWithProgress(
    endpoint: string,
    formData: FormData,
    onProgress?: (percent: number) => void
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('authToken');
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            resolve(xhr.responseText);
          }
        } else {
          reject(new Error(`API request failed: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

      xhr.open('POST', `${API_BASE_URL}${endpoint}`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    const token = localStorage.getItem('authToken');
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', response.status, errorText);
      throw new Error(`API request failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  }

  async uploadFileForPreview(file: File, delimiter?: string): Promise<ExcelData & { fileId: string }> {
    const formData = new FormData();
    formData.append('file', file);

    if (delimiter) {
      formData.append('delimiter', delimiter);
    }

    const result = await this.makeRequest('/excel/upload-preview', {
      method: 'POST',
      body: formData,
    });

    return result.data;
  }

  async uploadFile(
    file: File,
    options: {
      status?: 'uploaded' | 'processing' | 'processed' | 'calculated' | 'error' | 'final';
      mappings?: ColumnMappingData;
      schemaId?: string;
      companyId?: number;
      onProgress?: (percent: number) => void;
      schemaName?: string;
      settings?: { hasHeader: boolean; useStreetDistances: boolean; standard: string };
      delimiter?: string;
    } = {},
  ): Promise<ExcelData & { fileId: string }> {
    const {
      status = 'final',
      mappings,
      schemaId,
      companyId,
      onProgress,
      schemaName,
      settings,
      delimiter,
    } = options;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('status', status);

    if (mappings) {
      formData.append('mappings', JSON.stringify(mappings));
    }

    if (schemaId) {
      formData.append('schemaId', schemaId);
    }

    if (companyId) {
      formData.append('companyId', companyId.toString());
    }

    if (schemaName) {
      formData.append('schemaName', schemaName);
    }

    if (settings) {
      formData.append('settings', JSON.stringify(settings));
    }

    if (delimiter) {
      formData.append('delimiter', delimiter);
    }

    const result = await this.uploadWithProgress('/excel/upload', formData, onProgress);
    return result.data;
  }

  async processFile(fileOrId: File | string, mappings: ColumnMappingData, settings: SchemaData['settings'], companyId: number, schemaName?: string, skipRowIndices?: number[], onProgress?: (percent: number) => void, delimiter?: string): Promise<any> {
    const formData = new FormData();

    if (fileOrId instanceof File) {
      formData.append('file', fileOrId);
    } else {
      formData.append('fileId', fileOrId);
    }

    formData.append('mappings', JSON.stringify(mappings));
    formData.append('settings', JSON.stringify(settings));
    formData.append('companyId', companyId.toString());
    if (schemaName) {
      formData.append('schemaName', schemaName);
    }
    if (skipRowIndices && skipRowIndices.length > 0) {
      formData.append('skipRowIndices', JSON.stringify(skipRowIndices));
    }
    if (delimiter) {
      formData.append('delimiter', delimiter);
    }

    const result = await this.uploadWithProgress('/excel/process', formData, onProgress);
    return result.data;
  }

  async createSchema(schema: SchemaData): Promise<SchemaData> {
    preflight(excelSchemaBody, schema);
    const result = await this.makeRequest('/excel/schemas', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(schema),
    });

    return result.data;
  }

  async getSchemas(): Promise<SchemaData[]> {
    const result = await this.makeRequest('/excel/schemas');
    return result.data;
  }

  async updateSchema(id: string, schema: SchemaData): Promise<SchemaData> {
    preflight(excelSchemaBody, schema);
    const result = await this.makeRequest(`/excel/schemas/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(schema),
    });
    return result.data;
  }

  async deleteSchema(id: string): Promise<void> {
    await this.makeRequest(`/excel/schemas/${id}`, {
      method: 'DELETE',
    });
  }

  async getFieldDefinitions(legCount: number = 1): Promise<any> {
    const result = await this.makeRequest(`/excel/field-definitions?legCount=${legCount}`);
    return result.data;
  }
}

export const excelApi = new ExcelApi();